import { createSelector } from "reselect";
import getOr from "lodash/fp/getOr";
import _intersection from "lodash/intersection";
import { RootState } from "../types";
import { FunctionDISPATCH, FunctionsStoreSlice } from "./reducers/types";
import { FlowioFunctionId, FunctionDict } from "../../globalTypes";
import { getFunctionTemplatesForTemplateId } from "../functionTemplates/selectors";
import { getFunctionIdsForTemplateId } from "../templateMetadata/selectors";
import { EMPTY_ARRAY } from "../../globalConsts";
import { WorkflowCommentTypeAPI } from "../../requests/functions/types";
import { isFunctionStandard, isFunctionWorkflow } from "./utils";

export const getFunctionsSlice = (state: RootState): FunctionsStoreSlice =>
  state.functions;

export const getIsFetchingAll = (state: RootState): FunctionsStoreSlice =>
  state.functions.isFetching;

export const getById = (state: RootState) => getFunctionsSlice(state).byId;

export const getIsFunctionRunning = (state: RootState) =>
  getFunctionsSlice(state).isFunctionRunning;

export const getFunctionDataFromId = (state: RootState, id: FlowioFunctionId) =>
  getById(state)[id];

export const getFunctionCommentType = (
  state: RootState,
  functionId: FlowioFunctionId
): WorkflowCommentTypeAPI =>
  getFunctionDataFromId(state, functionId)?.workflowCommentType ??
  WorkflowCommentTypeAPI.None;

export const getIsFetchingForFunctionId = (
  state: RootState,
  id: FlowioFunctionId
) => getOr(false, "isFetching", getFunctionDataFromId(state, id));

export const getIdsOfAllFunctions = createSelector(getById, (byId) =>
  Object.keys(byId).map(Number)
);

/* Vrací objekt ve tvaru globalTypes.ts/FunctionDict */
export const getFunctionDict = createSelector(
  getIdsOfAllFunctions,
  getById,
  (allIds, byId) =>
    allIds.reduce((acc, currId) => {
      const currentSfunction = byId[currId];
      if (Object.keys(currentSfunction).length === 1) return acc; // obsahuje jenom isLoading

      const { id, ...rest } = currentSfunction as FunctionDISPATCH;
      if (id !== currId)
        throw new Error(
          `Inside getFunctionDict selector: mismatch between function key (${currId}) and ID ${id}`
        );
      acc[currId] = { id: currId, ...rest };
      return acc;
    }, {} as FunctionDict)
);

// standard = non-hegWorkflow
// standrd function is function, which is not hegWorkflow, common and dont have display conditions
export const makeGetStandardFunctionIds = () =>
  createSelector(
    getFunctionIdsForTemplateId,
    getFunctionDict,
    getFunctionTemplatesForTemplateId,
    (functionIds, functionsById, functionTemplates): FlowioFunctionId[] => {
      if (!functionIds) return EMPTY_ARRAY;
      return functionIds.filter((functionId) =>
        isFunctionStandard({ functionId, functionsById, functionTemplates })
      );
    }
  );

export const makeGetWorkflowFunctionsIds = () =>
  createSelector(
    getFunctionIdsForTemplateId,
    getFunctionDict,
    getFunctionTemplatesForTemplateId,
    (functionIds, functionsById, functionTemplates): FlowioFunctionId[] => {
      if (!functionIds) return EMPTY_ARRAY;
      return functionIds.filter((functionId) =>
        isFunctionWorkflow({ functionId, functionsById, functionTemplates })
      );
    }
  );

export const makeGetCommonFunctionIds = () =>
  createSelector(
    getFunctionIdsForTemplateId,
    getFunctionDict,
    (functionIds, functionsById) => {
      if (!functionIds) return EMPTY_ARRAY;
      return functionIds.filter((id) => functionsById[id]?.isCommon);
    }
  );

export const makeGetLeftFunctionIds = () =>
  createSelector(
    getFunctionIdsForTemplateId,
    getFunctionTemplatesForTemplateId,
    (functionIds, functionTemplates): FlowioFunctionId[] => {
      if (!functionIds || !functionTemplates) return EMPTY_ARRAY;

      return functionIds.filter(
        (id) => !!functionTemplates[String(id)]?.overrideLeftGesture
      );
    }
  );

export const makeGetRightFunctionIds = () =>
  createSelector(
    getFunctionIdsForTemplateId,
    getFunctionTemplatesForTemplateId,
    (functionIds, functionTemplates): FlowioFunctionId[] => {
      if (!functionIds || !functionTemplates) return EMPTY_ARRAY;

      return functionIds.filter(
        (id) => !!functionTemplates[String(id)]?.overrideRightGesture
      );
    }
  );

export const makeGetLeftStandardFunctionIds = () =>
  createSelector(
    makeGetLeftFunctionIds(),
    makeGetStandardFunctionIds(),
    _intersection
  );

export const makeGetRightStandardFunctionIds = () =>
  createSelector(
    makeGetRightFunctionIds(),
    makeGetStandardFunctionIds(),
    _intersection
  );
